import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import {
  useSensorStream,
  useSensorStreamsInfinite,
} from '../hooks/useSensorEdit';

interface SensorStreamsComboboxProps {
  value?: string;
  onChange: (streamId: string) => void;
  organisationId?: string | null;
  currentSensorId?: string | null;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

const ALL_VALUE = 'all';

export function SensorStreamsCombobox({
  value,
  onChange,
  organisationId,
  currentSensorId,
  disabled = false,
  className,
  placeholder = 'No stream',
}: SensorStreamsComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const infiniteQuery = useSensorStreamsInfinite({
    search: debouncedSearch,
    organisationId,
    currentSensorId,
  });

  const loadedStreams = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.streams) ?? [],
    [infiniteQuery.data],
  );
  const total = infiniteQuery.data?.pages[0]?.total ?? 0;

  const isExplicitValue = !!value && value !== ALL_VALUE;
  const selectedFromLoaded = isExplicitValue
    ? loadedStreams.find((stream) => stream._id === value)
    : undefined;
  const fallbackLookupId = isExplicitValue && !selectedFromLoaded ? value : undefined;
  const fallbackQuery = useSensorStream(fallbackLookupId);
  const selected = selectedFromLoaded ?? fallbackQuery.data ?? null;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          infiniteQuery.hasNextPage &&
          !infiniteQuery.isFetchingNextPage
        ) {
          void infiniteQuery.fetchNextPage();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    open,
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
    infiniteQuery.fetchNextPage,
  ]);

  const triggerLabel = selected?.name ?? placeholder;
  const showingLabel = total > 0 ? `Showing ${loadedStreams.length} of ${total}` : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || !organisationId}
          className={cn(
            'h-7 w-full justify-between text-xs font-normal',
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search streams..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {infiniteQuery.isError ? (
                <button
                  type="button"
                  className="w-full px-2 py-2 text-left text-xs"
                  onClick={() => {
                    void infiniteQuery.refetch();
                  }}
                >
                  Failed to load. Click to retry.
                </button>
              ) : infiniteQuery.isLoading ? (
                'Loading...'
              ) : (
                'No streams found.'
              )}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onChange(ALL_VALUE);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    !value || value === ALL_VALUE ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {placeholder}
              </CommandItem>
              {loadedStreams.map((stream) => (
                <CommandItem
                  key={stream._id}
                  value={stream._id}
                  onSelect={() => {
                    onChange(stream._id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === stream._id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{stream.name}</span>
                </CommandItem>
              ))}
              {infiniteQuery.hasNextPage && (
                <div
                  ref={sentinelRef}
                  className="flex items-center justify-center py-2 text-xs text-muted-foreground"
                >
                  {infiniteQuery.isFetchingNextPage ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {showingLabel && (
            <div className="border-t px-2 py-1 text-xs text-muted-foreground">
              {showingLabel}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
